import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {LocalVideoOptions} from '../../../types';
import {
    AboveFoldScrollIndicator,
    CmsButtonProps,
    Typography,
    Video,
    ImageProps,
    Button,
    VideoRatioWrapper,
    VerticalSpacer,
    SectionInnerGridContent,
    LightboxButtonProps,
    LightboxButton,
    GridContainer
} from '../../elements';
import Grid from '@material-ui/core/Grid';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {
    MouseHijack,
    CmsButtonPropsWithHoverType,
    WithCaption,
    WithCaptionProps,
    ScaleOnViewWrap,
    AnimateOnViewWrap
} from '../../wraps';
import debounce from 'lodash.debounce';
import {useBrowserSatisfies} from 'epic-ue-shared';

const PromoWrapper = styled.div`
    .paragraph {
        color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.common.black, p.theme.palette.neutral.stone)};
        text-align: center;
        span {
            text-align: right;
        }
    }
    .lightbox-btn {
        text-align: center;
    }
    .buttons-container {
        display: flex;
        justify-content: center;
        flex-flow: row nowrap;
        ${(p) => p.theme.breakpoints.down('sm')} {
            flex-flow: row wrap;
        }
        & > span {
            padding-left: ${(p) => p.theme.spacing(2)}px;
            padding-right: ${(p) => p.theme.spacing(2)}px;
            ${(p) => p.theme.breakpoints.down('sm')} {
                padding-top: ${(p) => p.theme.spacing(2)}px;
            }
        }
    }
    canvas {
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        border-radius: 100px;
        transform: translate3d(0, 0, 0);
    }
    .canvas-box-shadow {
        box-shadow: 0 0 80px 80px ${(p) => p.theme.palette.background.default} inset,
            0 0 25px 25px ${(p) => p.theme.palette.background.default} inset,
            0 0 80px 80px ${(p) => p.theme.palette.background.default} inset;
        background: radial-gradient(
            circle,
            ${(p) => p.theme.palette.background.default}00 10%,
            ${(p) => p.theme.palette.background.default} 95%
        );
        border-radius: 50px;
    }
    .scale-wrapper {
        position: relative;
        .canvas-container {
            transform: translate3d(0, 0, 0);
            animation: Opacity 5s ${(p) => p.theme.easing};

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }

            @keyframes Opacity {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 1;
                }
            }
            position: absolute;
            z-index: 0;
            padding: 0;
            height: 125%;
            width: 134%;
            top: -15%;
            left: -17%;
            right: -17%;
            bottom: -10%;
        }
    }

    .with-caption {
        position: relative;
    }

    .promo-title,
    .promo-content {
        position: relative;
        z-index: 2;
    }

    .main-video,
    .caption-gradient {
        z-index: 1;
    }
`;

export interface PromoProps {
    accentColor?: AccentColorOptions;
    image?: ImageProps;
    mediaOptions?: LocalVideoOptions;
    lightboxBtn?: LightboxButtonProps;
    learnMoreBtn?: CmsButtonProps;
    secondaryBtn?: CmsButtonProps;
    title?: string;
    description?: string;
    link?: CmsButtonPropsWithHoverType;
    caption?: WithCaptionProps;
}

export const Promo = ({
    accentColor,
    mediaOptions,
    learnMoreBtn,
    secondaryBtn,
    title,
    caption,
    description,
    lightboxBtn,
    link = {}
}: PromoProps): JSX.Element | null => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const videoRef = useRef<HTMLDivElement>(null);
    const raf = useRef<any>(null);
    const [showBackLight, setShowBackLight] = useState(false);
    const browserDisabled = useBrowserSatisfies({
        safari: '<14',
        'internet explorer': '>8',
        chrome: '<85',
        firefox: '<85'
    });

    const isMdDown = useBreakpointDown('md');

    const onFullScaleChange = useCallback(
        (fullScale) => {
            if (browserDisabled || isMdDown) return;
            setShowBackLight(fullScale);
        },
        [setShowBackLight, browserDisabled, isMdDown]
    );

    useEffect(() => {
        const video = videoRef?.current?.getElementsByTagName('video')[0];
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        const cw = Math.floor(canvas?.clientWidth || 0);
        const ch = Math.floor(canvas?.clientHeight || 0);

        if (canvas) {
            canvas.width = cw;
            canvas.height = ch;
        }

        const rerender = debounce(
            () => {
                if (context && video) {
                    context.filter = 'blur(50px)';
                    context.drawImage(video, 0, 0, cw, ch);
                }
            },
            100,
            {
                maxWait: 100
            }
        );

        if (!showBackLight && raf?.current) {
            rerender.cancel();
            cancelAnimationFrame(raf.current);
            raf.current = null;
            return;
        }

        const updateCanvas = () => {
            rerender();
            raf.current = requestAnimationFrame(updateCanvas);
        };

        updateCanvas();

        return () => {
            if (raf.current) {
                rerender.cancel();
                cancelAnimationFrame(raf.current);
            }
        };
    }, [showBackLight, canvasRef, videoRef, raf]);

    return (
        <PromoWrapper className="ue-promo">
            <GridContainer>
                <div className="promo-title">
                    <SectionInnerGridContent>
                        {title && (
                            <AnimateOnViewWrap translateY={30}>
                                <Typography variant="h2" big>
                                    {title}
                                </Typography>
                                <VerticalSpacer variant="heading" />
                            </AnimateOnViewWrap>
                        )}
                    </SectionInnerGridContent>
                </div>
                <AboveFoldScrollIndicator translateY={0} />
                <div className={'scale-wrapper'}>
                    <ScaleOnViewWrap
                        id="promo-img"
                        duration={0.1}
                        threshold={0.2}
                        minPercentVisible={0.2}
                        onFullScaleChange={onFullScaleChange}
                        disableFromTop={true}>
                        <WithCaption forceBelow={true} {...caption}>
                            <MouseHijack
                                href={link.href}
                                hoverCursorType={link.hoverCursorType || 'play'}
                                accentColor={accentColor}>
                                <VideoRatioWrapper className="main-video" ref={videoRef}>
                                    <Video mediaOptions={mediaOptions} gradient={false} />
                                </VideoRatioWrapper>
                            </MouseHijack>
                            {showBackLight && (
                                <VideoRatioWrapper className="backlight-video canvas-container">
                                    <canvas ref={canvasRef} />
                                    <div className={'canvas-box-shadow'} />
                                </VideoRatioWrapper>
                            )}
                        </WithCaption>
                    </ScaleOnViewWrap>
                </div>
            </GridContainer>

            <VerticalSpacer variant="custom" spacing={5} />
            <div className="promo-content">
                {description && (
                    <GridContainer>
                        <Grid container justifyContent="center">
                            <Grid item xs={12} md={6}>
                                <AnimateOnViewWrap translateY={30}>
                                    <Typography variant="p">{description}</Typography>

                                    {lightboxBtn && lightboxBtn.label && (
                                        <>
                                            <VerticalSpacer variant="custom" spacing={4} />
                                            <LightboxButton
                                                {...lightboxBtn}
                                                accentColor={accentColor}
                                            />
                                        </>
                                    )}

                                    {(learnMoreBtn && learnMoreBtn.label) ||
                                    (secondaryBtn && secondaryBtn.label) ? (
                                        <>
                                            <VerticalSpacer variant="custom" spacing={4} />
                                            <div className="buttons-container">
                                                {learnMoreBtn && learnMoreBtn.label ? (
                                                    <Button
                                                        type="secondary"
                                                        href={learnMoreBtn.href}
                                                        color={accentColor}>
                                                        {learnMoreBtn.label}
                                                    </Button>
                                                ) : (
                                                    ''
                                                )}
                                                {secondaryBtn && secondaryBtn.label ? (
                                                    <Button
                                                        type="secondary"
                                                        href={secondaryBtn.href}
                                                        color={accentColor}>
                                                        {secondaryBtn.label}
                                                    </Button>
                                                ) : (
                                                    ''
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        ''
                                    )}
                                </AnimateOnViewWrap>
                            </Grid>
                        </Grid>
                    </GridContainer>
                )}
            </div>
        </PromoWrapper>
    );
};
